import { Component, Vue } from 'vue-property-decorator';
import ContributionLevelIcon from '@/components/common/contribution-level-icon/ContributionLevelIcon.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import LevelRangeDetails from '@/components/pages/profile/level-range-details/LevelRangeDetails.vue';
import ProjectCardsPagination from '@/components/pages/project-list/project-cards-pagination/ProjectCardsPagination.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import { CONTRIBUTION_LEVELS } from '@/constants';
import { ContributionLevel, PromotionInfo, RisingStarDTO, UserDTO } from '@/models';
import { fetchPromotionInfo } from '@/services';
import { formatDateToDisplay, getNumberOfSkipLevelByKey } from '@/helpers';

interface RisingStarsGridUser {
  currentLevelTop: string;
  prevLevelTop: string;
  levelUpHeight: string;
  levelUpTop: string;
  user: UserDTO;
}

@Component({
  components: {
    ContributionLevelIcon,
    InlineLoader,
    LevelRangeDetails,
    ProjectCardsPagination,
    UserInformation
  }
})
export default class RisingStars extends Vue {
  private levels: ContributionLevel[] = Object.values(CONTRIBUTION_LEVELS).reverse();
  private promotionInfo: PromotionInfo;
  private gridData: RisingStarsGridUser[] = [];
  private dataFetching = true;
  private spacings = {
    arrowHeight: 9.5,
    arrowPaddingY: 4,
    levelPaddingTop: 5,
    lineHeight: 48
  };
  
  async created(): Promise<void> {
    const { data: promotionInfo } = await fetchPromotionInfo();
    this.promotionInfo = promotionInfo;
    this.gridData = this.getGridData(promotionInfo?.risingStars || []);
    this.dataFetching = false;
  }
  
  get assessmentShown(): boolean {
    return !this.dataFetching && this.promotionInfo && !!this.promotionInfo?.startDate && !!this.promotionInfo?.endDate;
  }
  
  get assessmentDates(): string {
    const { promotionInfo } = this;
    return `${formatDateToDisplay(promotionInfo.startDate || '')} - ${formatDateToDisplay(promotionInfo.endDate || '')}`;
  }
  
  calculateLineTop = (idx: number): string => `${this.spacings.lineHeight * idx}px`;
  
  calculateArrowTop = (idx: number): string => {
    const { spacings } = this;
    const top = spacings.lineHeight * (idx + 0.5) - (spacings.arrowHeight / 2);
    return `${top}px`;
  }
  
  findIdxFromName(levelName: string): number {
    return this.levels.findIndex(level => level.name === levelName);
  }
  
  getGridData(promotedUsers: RisingStarDTO[]): RisingStarsGridUser[] {
    const { spacings } = this;
    return promotedUsers
      .sort((u1, u2) => {
        const numberSkipLevel1 = getNumberOfSkipLevelByKey(u1.previousLevel, u1.currentLevel);
        const numberSkipLevel2 = getNumberOfSkipLevelByKey(u2.previousLevel, u2.currentLevel);
        const compareLevel = numberSkipLevel2 - numberSkipLevel1;
        return compareLevel || u2.accumulatedDrops - u1.accumulatedDrops;
      })
      .map(user => {
        const currentLevel = CONTRIBUTION_LEVELS[user.currentLevel];
        const prevLevel = CONTRIBUTION_LEVELS[user.previousLevel];
        const currentLevelIdx = this.findIdxFromName(currentLevel.name);
        const prevLevelIdx = this.findIdxFromName(prevLevel.name);
        const skipLevel = prevLevelIdx - currentLevelIdx;
  
        const currentLevelTop = currentLevelIdx * spacings.lineHeight + spacings.levelPaddingTop;
        const prevLevelTop = prevLevelIdx * spacings.lineHeight + spacings.levelPaddingTop;
  
        return {
          currentLevelTop: `${currentLevelTop}px`,
          prevLevelTop: `${prevLevelTop}px`,
          levelUpHeight: `${skipLevel * spacings.lineHeight}px`,
          levelUpTop: `${currentLevelIdx * spacings.lineHeight + 20}px`,
          user: user.user
        };
      });
  }
}
